/**
 * Her ucun döndürdüğü hata zarfı (`docs/spec/06-api-spec.md`, `11-logging-observability.md §3`):
 * problem+json'a ek olarak bir `errorCode` ve yanıtı sunucu loglarına bağlayan `correlationId`.
 */
export interface ProblemDetails {
  readonly status: number
  /** Sunucudaki `ErrorCodes` kataloğundan gelen, makine tarafından okunabilen sabit kod. */
  readonly errorCode: string
  /**
   * Okuyan kişi için yazılmış mesaj; olduğu gibi gösterilebilir.
   *
   * Zarfta bu ada sahip bir alan **yoktur**: RFC 9457 `detail` (ve başlık olarak `title`) kullanır.
   * Burada tek bir isme indirgeniyor ki ekranlar hangi alanın dolu olduğunu bilmek zorunda kalmasın.
   */
  readonly message: string
  /** Yanıtı sunucu log satırlarına bağlar — destek için hata ekranlarında gösterilir. */
  readonly correlationId: string
  /** Doğrulama hatasında alan bazlı mesajlar, alan adına göre. */
  readonly errors?: Readonly<Record<string, readonly string[]>>
}

/**
 * Başarısız bir istek ve taşıdığı zarf.
 *
 * Dönen bir değer değil de `Error` alt sınıfı olması bilinçli: TanStack Query başarıyı
 * başarısızlıktan, sorgu fonksiyonunun fırlatıp fırlatmadığına bakarak ayırır.
 */
export class ApiError extends Error {
  /** Constructor parametre özelliği yerine açıkça bildirildi: `erasableSyntaxOnly` açık. */
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(problem.message)
    this.name = 'ApiError'
    this.problem = problem
  }

  get status(): number {
    return this.problem.status
  }

  get errorCode(): string {
    return this.problem.errorCode
  }

  get correlationId(): string {
    return this.problem.correlationId
  }

  /** Doğrulama hatasındaki alan mesajları; doğrudan react-hook-form'a verilebilir. */
  get fieldErrors(): Readonly<Record<string, readonly string[]>> {
    return this.problem.errors ?? {}
  }
}

/** SPA'nın dallandığı hata kodları. Diğerleri kullanıcıya gösterilir, ayrıca yorumlanmaz. */
export const errorCodes = {
  /** Oturum yok ya da süresi dolmuş. Doğru tepki: yenile ve isteği tekrarla. */
  unauthorized: 'UNAUTHORIZED',

  /**
   * Bu istekte gönderilen bir sır reddedildi — şifre, doğrulama kodu, kurtarma kodu, bağlantı
   * token'ı. `unauthorized` ile aynı durum kodunu paylaşır ama tam tersi tepkiyi ister:
   * yenilemek burada oturumu düzeltmez, yalnız aynı yanlış sırrı ikinci kez gönderir.
   */
  invalidCredentials: 'INVALID_CREDENTIALS',

  forbidden: 'FORBIDDEN',
  validation: 'VALIDATION_ERROR',
  notFound: 'NOT_FOUND',
  conflict: 'CONFLICT',
  accountLocked: 'ACCOUNT_LOCKED',
  tenantSuspended: 'TENANT_SUSPENDED',
  passwordChangeRequired: 'PASSWORD_CHANGE_REQUIRED',
} as const

export type ErrorCode = (typeof errorCodes)[keyof typeof errorCodes]

/**
 * Başarısız bir yanıtı {@link ApiError}'a çevirir.
 *
 * Gövde beklediğimiz şekilde değilse üretilmiş bir zarfa düşer: zaman aşımına uğrayan bir proxy ya
 * da altyapı hata sayfası bizim handler'ımıza hiç uğramamıştır, ama arayüzün yine de bir durum
 * kodu ve mesaja ihtiyacı vardır — ayrıştırma hatasına değil.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  const fallback: ProblemDetails = {
    status: response.status,
    errorCode: 'UNKNOWN',
    message: 'Beklenmeyen bir hata oluştu.',
    correlationId: response.headers.get('X-Correlation-Id') ?? '',
  }

  try {
    const body: unknown = await response.json()

    if (!isProblemShaped(body)) {
      return new ApiError(fallback)
    }

    const fieldErrors = readFieldErrors(body.errors)

    return new ApiError({
      status: typeof body.status === 'number' ? body.status : response.status,
      errorCode: typeof body.errorCode === 'string' ? body.errorCode : fallback.errorCode,
      message: readMessage(body, fallback.message),
      correlationId:
        typeof body.correlationId === 'string' ? body.correlationId : fallback.correlationId,

      // Atama değil yayma (spread): `exactOptionalPropertyTypes` açıkken opsiyonel bir alan ya
      // hiç bulunmaz ya bir değer taşır — açıkça `undefined` olamaz.
      ...(fieldErrors === undefined ? {} : { errors: fieldErrors }),
    })
  } catch {
    // Gövde hiç JSON değilmiş. Eklenecek bir şey yok; burada fırlatmak, işe yarar bir
    // "sunucu 502 dedi" bilgisini işe yaramaz bir ayrıştırma hatasıyla değiştirirdi.
    return new ApiError(fallback)
  }
}

/**
 * Kullanıcıya gösterilecek metni RFC 9457 alanlarından okur.
 *
 * `detail` asıl açıklamadır, `title` özettir; sunucu ikisini de aynı metinle dolduruyor ama sıra
 * yine de anlamlı. `message` en sonda duruyor: zarfta böyle bir alan yok, ve bir zamanlar yalnız
 * onu okuduğumuz için sunucunun yazdığı her Türkçe mesaj ekranda genel metne düşüyordu.
 */
function readMessage(body: Record<string, unknown>, fallback: string): string {
  for (const candidate of [body.detail, body.title, body.message]) {
    if (typeof candidate === 'string' && candidate !== '') {
      return candidate
    }
  }

  return fallback
}

function isProblemShaped(body: unknown): body is Record<string, unknown> {
  return typeof body === 'object' && body !== null
}

function readFieldErrors(errors: unknown): Record<string, string[]> | undefined {
  if (typeof errors !== 'object' || errors === null) {
    return undefined
  }

  const result: Record<string, string[]> = {}

  for (const [field, messages] of Object.entries(errors)) {
    if (!Array.isArray(messages)) {
      continue
    }

    const readable = messages.filter((message): message is string => typeof message === 'string')

    // Mesajlarının tamamı okunamayan alan, boş listeyle tutulmak yerine tamamen düşürülür:
    // `{ age: [] }` verilen bir form, söyleyecek sözü olmayan bir hata işareti gösterir.
    if (readable.length > 0) {
      result[field] = readable
    }
  }

  return Object.keys(result).length > 0 ? result : undefined
}
